import React from 'react';
import {Button, GridContainer, Typography} from '../../elements';
import {
    LicenseContactButton,
    LicenseEyebrowBullets,
    LicenseEyebrowCaption,
    LicenseLinksCaption,
    LicenseTitleDescription
} from './atoms';
import {UeLicensesProps} from './UeLicenses.types';
import {UeLicenseCard, UeLicenseContactCard} from './UeLicenseCard.molecule';
import {useBreakpointDown} from 'epic-ue-theme';
import {AnimateOnViewWrap} from '../../wraps';
import {UeLicensesWrapper} from './UeLicenses.styles';
import {useLicensesRefs, useAlignLicenses} from './UeLicenses.hooks';

export const UeLicenses = (props: UeLicensesProps): JSX.Element | null => {
    const {
        className,
        accentColor,
        standardLicense = {},
        enterpriseLicense = {},
        customLicense = {},
        contactUs = {},
        onClick,
        hidden,
        minAnimateDelay = 1.0
    } = props;

    const isSm = useBreakpointDown('sm');
    const isMd = useBreakpointDown('md');
    const {refs, gridRef} = useLicensesRefs();
    const {standardWidth, titleHeight, priceHeight, includesHeight, bestForHeight} =
        useAlignLicenses(isMd, refs, gridRef);

    if (hidden) return null;
    return (
        <UeLicensesWrapper className={`${className || ''} ue-licenses`}>
            <GridContainer>
                <div className="ref-grid" ref={gridRef}>
                    <div className="outer-flex">
                        <div className="standard">
                            <AnimateOnViewWrap delay={minAnimateDelay} translateY={60}>
                                <UeLicenseCard
                                    ref={refs.standard.card}
                                    $width={standardWidth}
                                    className="standard-card"
                                    $activeBackgroundAccent={
                                        standardLicense.activeBackgroundAccent || 'forest'
                                    }>
                                    <LicenseTitleDescription
                                        ref={refs.standard.title}
                                        accentColor={accentColor}
                                        title={standardLicense.title}
                                        description={standardLicense.description}
                                        height={titleHeight}
                                    />
                                    <LicenseEyebrowCaption
                                        ref={refs.standard.price}
                                        accentColor={accentColor}
                                        eyebrow={{text: standardLicense.price}}
                                        caption={standardLicense.priceNote}
                                        height={priceHeight}
                                    />
                                    <LicenseEyebrowBullets
                                        ref={refs.standard.includes}
                                        accentColor={accentColor}
                                        activeBackground
                                        eyebrow={{text: standardLicense.topBulletsEyebrow}}
                                        bullets={standardLicense.topBullets}
                                        height={includesHeight}
                                    />
                                    <LicenseEyebrowBullets
                                        ref={refs.standard.bestFor}
                                        accentColor={accentColor}
                                        activeBackground
                                        eyebrow={{text: standardLicense.bottomBulletsEyebrow}}
                                        bullets={standardLicense.bottomBullets}
                                        height={bestForHeight}
                                    />
                                    <LicenseLinksCaption
                                        accentColor={accentColor}
                                        links={standardLicense.bottomLinks}
                                        caption={standardLicense.bottomCaption}
                                        buttonHref={standardLicense.buttonHref}
                                        buttonText={standardLicense.buttonText}
                                    />
                                </UeLicenseCard>
                            </AnimateOnViewWrap>
                        </div>
                        <AnimateOnViewWrap delay={minAnimateDelay} translateY={60}>
                            <div className="right">
                                <div className="top">
                                    <UeLicenseCard>
                                        <LicenseTitleDescription
                                            ref={refs.enterprise.title}
                                            accentColor={accentColor}
                                            title={enterpriseLicense.title}
                                            description={enterpriseLicense.description}
                                            height={titleHeight}
                                        />
                                        <LicenseEyebrowCaption
                                            ref={refs.enterprise.price}
                                            accentColor={accentColor}
                                            eyebrow={{text: enterpriseLicense.price}}
                                            caption={enterpriseLicense.priceNote}
                                            height={priceHeight}
                                        />
                                        <LicenseEyebrowBullets
                                            ref={refs.enterprise.includes}
                                            accentColor={accentColor}
                                            eyebrow={{text: enterpriseLicense.topBulletsEyebrow}}
                                            bullets={enterpriseLicense.topBullets}
                                            height={includesHeight}
                                        />
                                        <LicenseEyebrowBullets
                                            ref={refs.enterprise.bestFor}
                                            accentColor={accentColor}
                                            eyebrow={{text: enterpriseLicense.bottomBulletsEyebrow}}
                                            bullets={enterpriseLicense.bottomBullets}
                                            height={bestForHeight}
                                        />
                                        {isSm && (
                                            <LicenseContactButton
                                                accentColor={accentColor}
                                                contactDescription={contactUs?.contactDescription}
                                                contactButtonText={contactUs?.contactButtonText}
                                                onClick={onClick}
                                            />
                                        )}
                                    </UeLicenseCard>
                                    <UeLicenseCard>
                                        <LicenseTitleDescription
                                            ref={refs.custom.title}
                                            accentColor={accentColor}
                                            title={customLicense.title}
                                            description={customLicense.description}
                                            height={titleHeight}
                                        />
                                        <LicenseEyebrowCaption
                                            ref={refs.custom.price}
                                            accentColor={accentColor}
                                            eyebrow={{text: customLicense.price}}
                                            caption={customLicense.priceNote}
                                            height={priceHeight}
                                        />
                                        <LicenseEyebrowBullets
                                            ref={refs.custom.includes}
                                            accentColor={accentColor}
                                            eyebrow={{text: customLicense.topBulletsEyebrow}}
                                            bullets={customLicense.topBullets}
                                            height={includesHeight}
                                        />
                                        <LicenseEyebrowBullets
                                            ref={refs.custom.bestFor}
                                            accentColor={accentColor}
                                            eyebrow={{text: customLicense.bottomBulletsEyebrow}}
                                            bullets={customLicense.bottomBullets}
                                            height={bestForHeight}
                                        />
                                        {isSm && (
                                            <LicenseContactButton
                                                accentColor={accentColor}
                                                contactDescription={contactUs?.contactDescription}
                                                contactButtonText={contactUs?.contactButtonText}
                                                onClick={onClick}
                                            />
                                        )}
                                    </UeLicenseCard>
                                </div>
                                {!isSm && (
                                    <div className="bottom">
                                        <UeLicenseContactCard>
                                            <Typography
                                                variant="p"
                                                caption
                                                accentColor={accentColor}>
                                                {contactUs?.contactDescription}
                                            </Typography>
                                            <Button color={accentColor} onClick={onClick}>
                                                {contactUs?.contactButtonText}
                                            </Button>
                                        </UeLicenseContactCard>
                                    </div>
                                )}
                            </div>
                        </AnimateOnViewWrap>
                    </div>
                </div>
            </GridContainer>
        </UeLicensesWrapper>
    );
};
